// ──────────────────────────────────────────────────────────────────────
// Upcoming Gigs widget — canonical consumer of the Fretless design system.
//
//   Card → WidgetHeader → (loading | empty | list of ListRow)
//
// SharePopover is passed in from the dashboard so its self-contained
// share-link logic stays where it lives.
// ──────────────────────────────────────────────────────────────────────

import { format, parseISO } from "date-fns";
import { Link } from "react-router-dom";
import { ChevronRight, Eye, Music2, Plus } from "lucide-react";
import { DragHandle } from "@/hooks/useDragHandle";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import {
  Card,
  Band,
  Kicker,
  GhostBtn,
  WidgetHeader,
  ListRow,
} from "@/design-system/primitives";
import { tokens } from "@/design-system/tokens";

const { color, font, space, radius, border } = tokens;

// ───── Loading skeleton ─────────────────────────────────────────────
// Three pulsing rows on the inner surface tone.
function LoadingState() {
  return (
    <Band last padding={`${space.lg}px ${space.xl}px`}>
      <div style={{ display: "flex", flexDirection: "column", gap: space.md }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              height: 76,
              background: color.innerBg,
              border: border.inner,
              borderRadius: radius.lg,
              animation: "flGigSkeleton 1.4s ease-in-out infinite",
              opacity: 0.7,
            }}
          />
        ))}
      </div>
      <style>{`
        @keyframes flGigSkeleton {
          0%, 100% { opacity: 0.45; }
          50%      { opacity: 0.85; }
        }
      `}</style>
    </Band>
  );
}

// ───── Empty state ──────────────────────────────────────────────────
function EmptyState({ navigate }) {
  return (
    <Band last padding={`${space.xxl}px ${space.xl}px`}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: space.md,
          textAlign: "center",
        }}
      >
        <Music2
          style={{
            width: 48,
            height: 48,
            color: color.textMuted,
            marginBottom: space.xs,
          }}
        />
        <p
          style={{
            fontFamily: font.body,
            fontSize: 14.5,
            lineHeight: 1.55,
            color: color.textMuted,
            margin: 0,
          }}
        >
          No upcoming gigs yet
        </p>
        <GhostBtn
          primary
          icon={Plus}
          label="Add your first gig"
          onClick={() => navigate("/gigs/new")}
          data-testid="add-first-gig"
        />
      </div>
    </Band>
  );
}

// ───── Date block ───────────────────────────────────────────────────
// Left-anchored date stack used inside each ListRow.
// Both kickers use textDim (matches the month brightness — never textMuted).
// `compact` shrinks the block to 44px (was 58) and the numeral to 20px
// (was 24) — gives the artist/venue text more room on narrow mobile.
function DateBlock({ date, compact = false }) {
  const width = compact ? 44 : 58;
  const numeralSize = compact ? 20 : 24;
  let parsed = null;
  if (date) {
    try {
      parsed = parseISO(date);
      if (isNaN(parsed.getTime())) parsed = null;
    } catch {
      parsed = null;
    }
  }
  if (!parsed) {
    return (
      <div style={{ width, flexShrink: 0 }}>
        <Kicker size={11} color={color.textMuted}>TBA</Kicker>
      </div>
    );
  }
  return (
    <div
      style={{
        width,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 2,
      }}
    >
      <Kicker size={11} color={color.textDim}>
        {format(parsed, "EEE").toUpperCase()}
      </Kicker>
      <div
        style={{
          fontFamily: font.body,
          fontWeight: 600,
          fontSize: numeralSize,
          lineHeight: 1,
          color: color.text,
          letterSpacing: "-0.02em",
        }}
      >
        {format(parsed, "d")}
      </div>
      <Kicker size={11} color={color.textDim}>
        {format(parsed, "MMM").toUpperCase()}
      </Kicker>
    </div>
  );
}

// ───── Trailing slot for a gig row ──────────────────────────────────
// Desktop: share icon + amber start-time + green fee stacked.
// Mobile (compact): every trailing element is hidden — share, start
// time AND fee — so the full row width is reserved for the date block
// + artist/venue text. Tap the row to see fee/time in the detail view.
function RowTrailing({ gig, SharePopover, compact = false }) {
  if (compact) return null;
  const hasFee = gig.fee != null && gig.fee !== "";
  return (
    <>
      {gig.share_enabled && gig.share_token && SharePopover && (
        <SharePopover shareToken={gig.share_token} gigId={gig.gig_id} gig={gig} />
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 4,
          minWidth: 64,
        }}
      >
        {gig.start_time ? (
          <Kicker size={12} color={color.amber}>
            {gig.start_time}
          </Kicker>
        ) : (
          <Kicker size={12} color={color.textMuted}>TBD</Kicker>
        )}
        {hasFee && (
          <span
            style={{
              fontFamily: font.body,
              fontWeight: 600,
              fontSize: 13,
              color: color.green,
              letterSpacing: "-0.01em",
            }}
          >
            £{gig.fee}
          </span>
        )}
      </div>
    </>
  );
}

// ───── List band ────────────────────────────────────────────────────
function ListBand({ gigs, navigate, SharePopover, compact = false }) {
  // Mobile: tighter outer padding so each row gets +16px of horizontal width
  // for the artist/venue text. Desktop is unchanged.
  const bandPadding = compact
    ? `${space.sm}px ${space.md}px ${space.md}px`
    : `${space.md}px ${space.xl}px ${space.xl}px`;
  // Mobile: tighter row padding + smaller leading/trailing gap.
  const rowStyle = compact
    ? { padding: `${space.sm}px ${space.md}px`, gap: space.md }
    : undefined;
  return (
    <Band last padding={bandPadding}>
      <div style={{ display: "flex", flexDirection: "column", gap: space.sm }}>
        {gigs.map((gig) => {
          const secondaryParts = [];
          if (gig.venue_name) secondaryParts.push(gig.venue_name);
          if (gig.city) secondaryParts.push(gig.city);
          return (
            <ListRow
              key={gig.gig_id}
              data-testid={`dashboard-gig-${gig.gig_id}`}
              style={rowStyle}
              leading={<DateBlock date={gig.date} compact={compact} />}
              primary={gig.artist_name || gig.venue_name || "Untitled gig"}
              secondary={secondaryParts.join(" · ")}
              trailing={<RowTrailing gig={gig} SharePopover={SharePopover} compact={compact} />}
              onClick={() => navigate(`/gigs/${gig.gig_id}/edit`)}
            />
          );
        })}
      </div>
    </Band>
  );
}

// ───── Hide button (small icon-only) ───────────────────────────────
function HideButton({ onHide }) {
  return (
    <button
      type="button"
      onClick={onHide}
      aria-label="Hide widget"
      title="Hide widget"
      style={{
        width: 32,
        height: 32,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        color: color.textMuted,
        border: "none",
        borderRadius: radius.md,
        cursor: "pointer",
      }}
    >
      <Eye className="h-4 w-4" />
    </button>
  );
}

// ───── Public component ────────────────────────────────────────────
export function UpcomingGigs({
  gigs = [],
  loading = false,
  navigate,
  onHide,
  SharePopover,
}) {
  const isDesktop = useIsDesktop(900);
  const compact = !isDesktop;
  // Tighter header padding on mobile so the kicker title doesn't crowd
  // its actions and the row content directly underneath gets full width.
  const headerPadding = compact
    ? `${space.md}px ${space.md}px`
    : undefined;
  return (
    <Card
      maxWidth="none"
      style={{ height: "100%" }}
      data-testid="upcoming-gigs-card"
    >
      <WidgetHeader
        title="Upcoming gigs"
        leading={<DragHandle />}
        padding={headerPadding}
        actions={
          <>
            <Link to="/gigs" style={{ textDecoration: "none" }}>
              <GhostBtn
                icon={ChevronRight}
                label="View all"
                data-testid="view-all-gigs"
              />
            </Link>
            <HideButton onHide={onHide} />
          </>
        }
      />
      {loading ? (
        <LoadingState />
      ) : gigs.length === 0 ? (
        <EmptyState navigate={navigate} />
      ) : (
        <ListBand
          gigs={gigs}
          navigate={navigate}
          SharePopover={SharePopover}
          compact={compact}
        />
      )}
    </Card>
  );
}

export default UpcomingGigs;
